import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Injectable, Directive } from '@angular/core';
import { Subject } from 'rxjs';
import { createPopper } from '@popperjs/core';
import * as i1 from '@slidebean/hydra/dynamic';

class PopperHostComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopperHostComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PopperHostComponent, selector: "hy-popper-host", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopperHostComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'hy-popper-host',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }] });
class Popper {
    constructor(factory, view, detector, target, options, content) {
        this.factory = factory;
        this.view = view;
        this.detector = detector;
        this.target = target;
        this.options = options;
        this.content = content;
    }
    get isOpen() {
        return !!this.instance && !!this.ref;
    }
    get placement() {
        return this.instance?.state.placement ?? 'top';
    }
    show() {
        if (this.isOpen)
            return;
        this.render();
    }
    hide() {
        if (!this.isOpen)
            return;
        this.destroy();
    }
    setContent(content) {
        this.content = content;
        if (!this.isOpen)
            return;
        this.render();
    }
    setOptions(options) {
        this.options = options;
        if (!this.instance)
            return;
        this.instance.setOptions(this.options);
    }
    render() {
        if (!!this.instance)
            this.destroy();
        if (!!this.ref)
            this.ref.destroy();
        this.ref = this.factory.create(PopperHostComponent, {
            content: { value: this.content },
            container: this.view,
        });
        this.instance = createPopper(this.target, this.ref.element, this.options);
    }
    destroy() {
        if (!!this.instance) {
            this.instance.destroy();
            this.instance = void 0;
        }
        if (!!this.ref) {
            this.ref.destroy();
            this.ref = void 0;
            this.detector.detectChanges();
        }
    }
}
class PopperFactory {
    constructor(factory, view, detector) {
        this.factory = factory;
        this.view = view;
        this.detector = detector;
    }
    create(target, content, options) {
        return new Popper(this.factory, this.view, this.detector, target, options, content);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopperFactory, deps: [{ token: i1.DynamicComponentFactory }, { token: i0.ViewContainerRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopperFactory }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopperFactory, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.DynamicComponentFactory }, { type: i0.ViewContainerRef }, { type: i0.ChangeDetectorRef }] });
class PopperPrimitive {
    constructor(factory, element, zone) {
        this.factory = factory;
        this.element = element;
        this.zone = zone;
        this.destroy$ = new Subject();
    }
    ngAfterViewInit() {
        this.zone.runOutsideAngular(() => {
            this.popper = this.create(this.getTarget());
        });
    }
    async show() {
        if (!this.popper)
            return;
        this.popper.show();
    }
    async hide() {
        if (!this.popper)
            return;
        this.popper.hide();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.popper)
            this.popper.hide();
    }
    getTarget() {
        return this.element.nativeElement;
    }
    getContent() {
        return '';
    }
    getOptions() {
        return {};
    }
    create(element) {
        return this.factory.create(element, this.getContent(), this.getOptions());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopperPrimitive, deps: [{ token: PopperFactory }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: PopperPrimitive, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopperPrimitive, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: PopperFactory }, { type: i0.ElementRef }, { type: i0.NgZone }] });

/**
 * Generated bundle index. Do not edit.
 */

export { Popper, PopperFactory, PopperHostComponent, PopperPrimitive };
